/**
 * Serviço centralizado para chamadas à API
 * Facilita a manutenção e permite adicionar interceptors, tratamento de erros, etc.
 */

const getApiUrl = () => {
  return (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
};

/**
 * Função centralizada para limpar sessão e redirecionar para login
 * Usada quando token expira ou é inválido
 */
const clearSessionAndRedirect = () => {
  // Limpar todos os dados de autenticação
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('user');
  localStorage.removeItem('primeiro_acesso');
  localStorage.removeItem('user_roles');
  
  // Redirecionar para login
  window.location.href = '/login';
};

/**
 * Verifica se a resposta indica token expirado/inválido
 */
const isTokenExpired = (response, data) => {
  if (response.status === 401) {
    const mensagem = data?.mensagem || data?.message || '';
    return (
      mensagem.includes('Token inválido') ||
      mensagem.includes('Token expirado') ||
      mensagem.includes('token inválido') ||
      mensagem.includes('token expirado') ||
      mensagem.includes('Unauthorized') ||
      mensagem.toLowerCase().includes('invalid token') ||
      mensagem.toLowerCase().includes('expired token')
    );
  }
  return false;
};

/**
 * Formata uma data para o formato YYYY-MM-DD
 */
export const formatDate = (date) => {
  if (!date) return '';
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

/**
 * Constrói payload padronizado para requisições
 */
export const buildPayload = ({ startDate, endDate, contrato, centro, equipe }) => {
  const payload = {
    'data-referencia-inicio': formatDate(startDate),
    'data-referencia-fim': formatDate(endDate)
  };

  // Adicionar filtros se não forem 'Todos' ou arrays vazios
  // Envia arrays diretamente para a API (backend aceita arrays)
  if (contrato && contrato !== 'Todos' && !Array.isArray(contrato)) {
    payload['des_contrato'] = contrato;
  } else if (Array.isArray(contrato) && contrato.length > 0 && !contrato.includes('Todos')) {
    payload['des_contrato'] = contrato; // Array direto
  }

  if (centro && centro !== 'Todos' && !Array.isArray(centro)) {
    payload['des_centro_servico'] = centro;
  } else if (Array.isArray(centro) && centro.length > 0 && !centro.includes('Todos')) {
    payload['des_centro_servico'] = centro; // Array direto
  }

  if (equipe && equipe !== 'Todos' && !Array.isArray(equipe)) {
    payload['des_equipe'] = equipe;
  } else if (Array.isArray(equipe) && equipe.length > 0 && !equipe.includes('Todos')) {
    payload['des_equipe'] = equipe; // Array direto
  }

  return payload;
};

/**
 * Obtém o token de autenticação do localStorage
 */
const getAuthToken = () => {
  return localStorage.getItem('access_token');
};

/**
 * Faz uma requisição POST para a API
 */
export const apiPost = async (endpoint, payload) => {
  const apiUrl = getApiUrl();
  const token = getAuthToken();
  
  // Incrementar contador de chamadas para testes de cache
  const counterKey = getCounterKey(endpoint, payload || {});
  requestCounters[counterKey] = (requestCounters[counterKey] || 0) + 1;
  if (process.env.NODE_ENV === 'development') {
    console.log('[Cache] apiPost chamada', { endpoint, count: requestCounters[counterKey] });
  }
  
  const headers = {
    'Content-Type': 'application/json'
  };
  
  // Adicionar token de autenticação se disponível
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  const response = await fetch(`${apiUrl}${endpoint}`, {
    method: 'POST',
    headers,
    body: JSON.stringify(payload)
  });

  // Se token expirou (401), tentar renovar
  if (response.status === 401 && token) {
    const data = await response.json().catch(() => ({}));
    
    // Verificar se a mensagem indica token expirado/inválido
    if (isTokenExpired(response, data)) {
      // Log para debug
      if (process.env.NODE_ENV === 'development') {
        console.warn('[apiPost] Token expirado detectado. Redirecionando para login...', {
          endpoint,
          mensagem: data?.mensagem || data?.message || ''
        });
      }
      // Token expirado - limpar e redirecionar para login
      clearSessionAndRedirect();
      throw new Error('Token expirado. Redirecionando para login...');
    }

    // Tentar renovar token se não for erro de token expirado
    const refreshToken = localStorage.getItem('refresh_token');
    if (refreshToken) {
      try {
        const refreshResponse = await fetch(`${apiUrl}/api/auth/refresh`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refresh_token: refreshToken })
        });
        
        if (refreshResponse.ok) {
          const refreshData = await refreshResponse.json();
          const newToken = refreshData.dados?.access_token;
          if (newToken) {
            localStorage.setItem('access_token', newToken);
            // Tentar novamente com o novo token
            const retryResponse = await fetch(`${apiUrl}${endpoint}`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${newToken}`
              },
              body: JSON.stringify(payload)
            });
            
            if (!retryResponse.ok) {
              const retryData = await retryResponse.json().catch(() => ({}));
              // Verificar se ainda é erro de token
              if (isTokenExpired(retryResponse, retryData)) {
                if (process.env.NODE_ENV === 'development') {
                  console.warn('[apiPost] Token ainda inválido após refresh. Redirecionando para login...');
                }
                clearSessionAndRedirect();
                throw new Error('Token expirado. Redirecionando para login...');
              }
              throw new Error(`Erro na API: ${retryResponse.status} - ${retryResponse.statusText}`);
            }
            
            return retryResponse.json();
          }
        }
      } catch (refreshError) {
        console.error('Erro ao renovar token:', refreshError);
        // Limpar tokens e redirecionar para login
        if (process.env.NODE_ENV === 'development') {
          console.warn('[apiPost] Erro ao renovar token. Redirecionando para login...');
        }
        clearSessionAndRedirect();
        throw new Error('Token expirado. Redirecionando para login...');
      }
    } else {
      // Sem refresh token - redirecionar para login
      if (process.env.NODE_ENV === 'development') {
        console.warn('[apiPost] Sem refresh token. Redirecionando para login...');
      }
      clearSessionAndRedirect();
      throw new Error('Token expirado. Redirecionando para login...');
    }
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    // Verificar se é erro de token expirado mesmo em outros status codes
    if (isTokenExpired(response, errorData)) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('[apiPost] Token expirado detectado em outro status code. Redirecionando para login...', {
          status: response.status,
          mensagem: errorData?.mensagem || errorData?.message || ''
        });
      }
      clearSessionAndRedirect();
      throw new Error('Token expirado. Redirecionando para login...');
    }
    throw new Error(errorData.mensagem || errorData.message || `Erro na API: ${response.status} - ${response.statusText}`);
  }

  return response.json();
};

/**
 * Faz uma requisição GET para a API
 */
export const apiGet = async (endpoint, params = {}) => {
  const apiUrl = getApiUrl();
  const token = getAuthToken();
  const queryString = new URLSearchParams(params).toString();
  const url = `${apiUrl}${endpoint}${queryString ? `?${queryString}` : ''}`;
  
  // Incrementar contador para GET também
  const counterKey = getCounterKey(endpoint, params || {});
  requestCounters[counterKey] = (requestCounters[counterKey] || 0) + 1;
  if (process.env.NODE_ENV === 'development') {
    console.log('[Cache] apiGet chamada', { endpoint, count: requestCounters[counterKey] });
  }
  
  const headers = {
    'Content-Type': 'application/json'
  };
  
  // Adicionar token de autenticação se disponível
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  const response = await fetch(url, {
    method: 'GET',
    headers
  });

  // Verificar se token expirou
  if (response.status === 401 && token) {
    const data = await response.json().catch(() => ({}));
    if (isTokenExpired(response, data)) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('[apiGet] Token expirado detectado. Redirecionando para login...', {
          endpoint,
          mensagem: data?.mensagem || data?.message || ''
        });
      }
      clearSessionAndRedirect();
      throw new Error('Token expirado. Redirecionando para login...');
    }
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    // Verificar se é erro de token expirado mesmo em outros status codes
    if (isTokenExpired(response, errorData)) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('[apiGet] Token expirado detectado. Redirecionando para login...', {
          status: response.status,
          mensagem: errorData?.mensagem || errorData?.message || ''
        });
      }
      clearSessionAndRedirect();
      throw new Error('Token expirado. Redirecionando para login...');
    }
    throw new Error(errorData.mensagem || errorData.message || `Erro na API: ${response.status} - ${response.statusText}`);
  }

  return response.json();
};

/**
 * Gera uma chave única para o cache baseada nos parâmetros da requisição
 * Usa serialização JSON para garantir consistência na comparação
 */
export const generateCacheKey = (endpoint, payload) => {
  // Criar um objeto ordenado para garantir consistência
  const sortedKeys = Object.keys(payload).sort();
  const sortedPayload = sortedKeys.reduce((acc, key) => {
    acc[key] = payload[key];
    return acc;
  }, {});
  
  // Serializar para garantir comparação correta no React Query
  // O React Query usa comparação estrutural, mas garantir serialização ajuda
  const cacheKey = [endpoint, sortedPayload];
  
  // Log para debug (apenas em desenvolvimento)
  if (process.env.NODE_ENV === 'development') {
    console.log('[Cache] Gerando chave:', { endpoint, payload: sortedPayload, key: JSON.stringify(cacheKey) });
  }
  
  return cacheKey;
};

/**
 * Instrumentação de requisições para testes de cache
 * Conta quantas vezes uma combinação endpoint+payload foi chamada
 */
const requestCounters = {};

const getCounterKey = (endpoint, payload) => {
  try {
    // Reutiliza o mesmo formato da chave de cache
    const key = JSON.stringify(generateCacheKey(endpoint, payload));
    return key;
  } catch (e) {
    return `${endpoint}|fallback`;
  }
};

export const getRequestCount = (endpoint, payload) => {
  const key = getCounterKey(endpoint, payload || {});
  return requestCounters[key] || 0;
};

export const getAllRequestCounts = () => {
  return { ...requestCounters };
};

